import type { BadgeProps } from '@/components/ui/badge'
import type { BriefSkill } from '@/hooks/use-briefs'
import { formatClientDate } from '@/lib/client-locale'

type BadgeVariant = NonNullable<BadgeProps['variant']>

export function briefStatusBadgeVariant(status: string): BadgeVariant {
  switch (status) {
    case 'active':
      return 'defaultSoft'
    case 'draft':
      return 'secondarySoft'
    case 'closed':
      return 'destructiveSoft'
    default:
      return 'secondarySoft'
  }
}

export function briefPriorityBadgeVariant(priority: string): BadgeVariant {
  switch (priority) {
    case 'high':
      return 'destructiveSoft'
    case 'medium':
      return 'defaultSoft'
    case 'low':
      return 'outline'
    default:
      return 'outline'
  }
}

export function formatBriefStatus(status: string): string {
  return status.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function formatBriefPriority(priority: string): string {
  return priority.replace(/\b\w/g, (c) => c.toUpperCase())
}

export type BriefSkillSummary = {
  requiredCount: number
  preferredCount: number
  countLine: string
  nameLine: string | null
}

export function summarizeBriefSkills(
  skills: BriefSkill[],
  options?: { maxNames?: number },
): BriefSkillSummary {
  const maxNames = options?.maxNames ?? 3
  const required = skills.filter((s) => s.requirement_type === 'required')
  const preferred = skills.filter((s) => s.requirement_type === 'preferred')

  const parts: string[] = []
  if (required.length > 0) {
    parts.push(`${required.length} required`)
  }
  if (preferred.length > 0) {
    parts.push(`${preferred.length} preferred`)
  }
  const countLine = parts.length > 0 ? parts.join(' · ') : 'No skills specified'

  const orderedNames = [
    ...required.map((s) => s.skill_name),
    ...preferred.map((s) => s.skill_name),
  ]
  const nameLine =
    orderedNames.length > 0
      ? orderedNames.slice(0, maxNames).join(', ') +
        (orderedNames.length > maxNames ? ` +${orderedNames.length - maxNames}` : '')
      : null

  return {
    requiredCount: required.length,
    preferredCount: preferred.length,
    countLine,
    nameLine,
  }
}

export function formatBriefDate(dateString: string): string {
  return formatClientDate(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function sortBriefsForDashboardPreview<T extends { status: string; updated_at: string }>(
  briefs: T[],
  limit = 4,
): T[] {
  const statusOrder: Record<string, number> = { active: 0, draft: 1, closed: 2 }
  return [...briefs]
    .sort((a, b) => {
      const statusDiff = (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9)
      if (statusDiff !== 0) return statusDiff
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    })
    .slice(0, limit)
}

/** Opens create-brief canvas on the briefs list page. */
export const BRIEFS_CREATE_HREF = '/recruiter/briefs?create=1'
